// frontend/src/components/AGTHealth/utils/AGTFloatingChat.js
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Global, css } from '@emotion/react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  styled,
  alpha,
  InputBase,
  ClickAwayListener
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
  Minimize2,
  ArrowUp,
  Stethoscope,
  Calendar,
  Pill,
  Activity
} from 'lucide-react';
import agthealthMedicalAssistantService from '../services/agthealthMedicalAssistantService';
import AppleSpinner from '../../common/AppleSpinner';
import { useTranslation } from 'react-i18next';

// Inyección de estilos globales para animaciones personalizadas
const GlobalStyles = () => (
  <Global
    styles={css`
      @keyframes fade_eiknz_10 {
        0% {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      ._fadeIn_eiknz_10 {
        animation: fade_eiknz_10 var(--duration, 0s) ease forwards calc(10ms + var(--delay, 0s));
        opacity: 0;
      }
      @keyframes fade_1frq2_1 {
        0% {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      ._markdown_1frq2_10.markdown .katex-display {
        animation: fade_1frq2_1 0.4s ease 50ms forwards;
        opacity: 0;
      }
    `}
  />
);

// Sistema de colores (actualizado para AGTHealth)
const appleColors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey3: '#C7C7CC',
      green: '#31C859',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { primaryElevated: '#FFFFFF' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#4CAF50', base: '#F2F2F7' } // Verde para AGTHealth
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey3: '#464649',
      green: '#2DD257',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { primaryElevated: '#000000' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#4CAF50', base: '#272729' } // Verde para AGTHealth
  }
};

const navigationColors = (theme) => {
  const mode = theme.palette.mode;
  const c = mode === 'light' ? appleColors.light : appleColors.dark;
  return {
    background: c.bg.primaryElevated,
    textDefault: c.label.primary,
    textSecondary: c.system.grey,
    cardBackground: c.ui.base,
    borderColor: c.system.grey3,
    accent: c.ui.accent,
    success: c.system.green,
    error: '#FF3B30',
    warning: '#FF9500'
  };
};

// Keyframe para la animación de fade in (usado en AnimatedWord)
const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

// Componente para palabra animada
const AnimatedWord = styled('span')(({ theme, delay, isPlaying }) => ({
  display: 'inline-block',
  marginRight: '0.25em',
  fontSize: 'inherit',
  opacity: isPlaying ? 0 : 1,
  animation: isPlaying ? `${fadeIn} 0.4s ease forwards ${delay}s` : 'none'
}));

// NUEVO COMPONENTE: FormattedAnimatedMessage
// Componente actualizado para respetar espacios y saltos de línea en markdown
const FormattedAnimatedMessage = ({ content, isPlaying }) => {
  const wordCountRef = useRef(0);
 
  // Función auxiliar para envolver cada palabra en AnimatedWord
  // Memorizamos la función con useCallback
  const wrapWordsInAnimation = useCallback((text) => {
    return text.split(' ').map((word) => {
      const currentIndex = wordCountRef.current++;
      return (
        <AnimatedWord
          key={`${word}-${currentIndex}`}
          delay={0.05 * currentIndex}
          isPlaying={isPlaying}
        >
          {word}{' '}
        </AnimatedWord>
      );
    });
  }, [isPlaying]); // Dependencia: isPlaying, ya que se usa dentro de la función

  // Utilizamos useMemo para memorizar el contenido animado y evitar re-procesarlo en cada render
  const animatedContent = useMemo(() => {
    // Reiniciamos el contador solo cuando el contenido cambia
    wordCountRef.current = 0;
    return (
      <Box style={{ '--duration': '0.4s', '--delay': '0s' }}>
        <ReactMarkdown
          components={{
            p: ({ children, node, ...props }) => (
              <p style={{ marginBottom: '1em' }}>
                {React.Children.map(children, (child) => {
                  if (typeof child === 'string') {
                    return wrapWordsInAnimation(child);
                  }
                  return child;
                })}
              </p>
            ),
            strong: ({ children }) => (
              <strong>
                {React.Children.map(children, (child) => {
                  if (typeof child === 'string') {
                    return wrapWordsInAnimation(child);
                  }
                  return child;
                })}
              </strong>
            ),
            em: ({ children }) => (
              <em>
                {React.Children.map(children, (child) => {
                  if (typeof child === 'string') {
                    return wrapWordsInAnimation(child);
                  }
                  return child;
                })}
              </em>
            ),
            li: ({ children }) => (
              <li style={{ marginBottom: '0.5em' }}>
                {React.Children.map(children, (child) => {
                  if (typeof child === 'string') {
                    return wrapWordsInAnimation(child);
                  }
                  return child;
                })}
              </li>
            ),
            h1: ({ children }) => (
              <h1 style={{ marginBottom: '0.75em', marginTop: '1em' }}>
                {React.Children.map(children, (child) => {
                  if (typeof child === 'string') {
                    return wrapWordsInAnimation(child);
                  }
                  return child;
                })}
              </h1>
            ),
            h2: ({ children }) => (
              <h2 style={{ marginBottom: '0.75em', marginTop: '1em' }}>
                {React.Children.map(children, (child) => {
                  if (typeof child === 'string') {
                    return wrapWordsInAnimation(child);
                  }
                  return child;
                })}
              </h2>
            ),
            pre: ({ children }) => (
              <pre style={{ marginBottom: '1em', marginTop: '0.5em', whiteSpace: 'pre-wrap' }}>
                {children}
              </pre>
            ),
            code: ({ inline, children, ...props }) => {
              if (inline) {
                return <code {...props}>{children}</code>;
              }
              return (
                <code {...props} style={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                  {children}
                </code>
              );
            },
            // Asegurarnos de que los saltos de línea se respeten
            br: () => <br />
          }}
        >
          {content}
        </ReactMarkdown>
      </Box>
    );
  }, [content, wrapWordsInAnimation]); // Ahora incluye wrapWordsInAnimation como dependencia

  if (isPlaying) {
    return animatedContent;
  }

  // Versión no animada con espaciados correctos
  return (
    <ReactMarkdown
      components={{
        p: ({ children }) => <p style={{ marginBottom: '1em' }}>{children}</p>,
        li: ({ children }) => <li style={{ marginBottom: '0.5em' }}>{children}</li>,
        h1: ({ children }) => <h1 style={{ marginBottom: '0.75em', marginTop: '1em' }}>{children}</h1>,
        h2: ({ children }) => <h2 style={{ marginBottom: '0.75em', marginTop: '1em' }}>{children}</h2>,
        pre: ({ children }) => <pre style={{ marginBottom: '1em', marginTop: '0.5em', whiteSpace: 'pre-wrap' }}>{children}</pre>,
        code: ({ inline, children, ...props }) => {
          if (inline) {
            return <code {...props}>{children}</code>;
          }
          return (
            <code {...props} style={{ display: 'block', whiteSpace: 'pre-wrap' }}>
              {children}
            </code>
          );
        },
        br: () => <br />
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

// Hook para manejar el último mensaje del asistente
const useLatestMessage = () => {
  const [latestMessageId, setLatestMessageId] = useState(null);

  const updateLatestMessage = useCallback((messages) => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.type === 'assistant') {
      setLatestMessageId(messages.length - 1);
      // Aumentamos el timeout a 10000ms para permitir que la animación finalice
      setTimeout(() => {
        setLatestMessageId(null);
      }, 10000);
    }
  }, []);

  return { latestMessageId, updateLatestMessage };
};

// Botón circular para enviar, con transición en opacidad
const CircularSendButton = styled(IconButton)(({ theme }) => {
  const navColors = navigationColors(theme);
  return {
    background: navColors.accent,
    color: navColors.textSecondary,
    borderRadius: '50%',
    width: 28,
    height: 28,
    padding: 4,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeInOut
    }),
    '&:hover': {
      background: alpha(navColors.accent, 0.9)
    }
  };
});

// Componente unificado para input/botón
const UnifiedInputButton = styled(Box)(({ theme, isexpanded }) => {
  const navColors = navigationColors(theme);
  return {
    width: '360px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: navColors.cardBackground,
    borderRadius: '20px',
    padding: '12px',
    transition: theme.transitions.create(['box-shadow', 'background-color'], {
      duration: theme.transitions.duration.complex,
      easing: theme.transitions.easing.easeInOut
    }),
    cursor: 'pointer',
    pointerEvents: 'auto',
    border: `1px solid ${navColors.borderColor}`,
    '& *': {
      cursor: isexpanded === 'true' ? 'text' : 'pointer'
    }
  };
});

// Input estilizado
const StyledInputBase = styled(InputBase)(({ theme, isexpanded }) => {
  const navColors = navigationColors(theme);
  return {
    width: '100%',
    color: navColors.textDefault,
    '& .MuiInputBase-input': {
      padding: '0px',
      cursor: isexpanded === 'true' ? 'text' : 'pointer',
      '&::placeholder': {
        color: navColors.textSecondary,
        opacity: 1
      }
    }
  };
});

// Chat container con responsividad
const ChatContainer = styled(Paper)(({ theme }) => {
  const navColors = navigationColors(theme);
  const isSmallScreen = useMediaQuery('(max-height: 700px)');
  
  return {
    borderRadius: theme.shape.borderRadius * 2,
    background: navColors.background,
    width: '360px',
    // Ajuste de altura basado en el tamaño de pantalla
    height: isSmallScreen ? '450px' : '600px',
    maxHeight: '80vh', // Garantiza que nunca exceda el 80% de la altura de la ventana
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'none',
    border: `1px solid ${navColors.borderColor}`,
    transition: theme.transitions.create(['opacity', 'transform'], {
      duration: theme.transitions.duration.complex,
      easing: theme.transitions.easing.easeInOut
    })
  };
});

// Encabezado del chat
const ChatHeader = styled(Box)(({ theme }) => {
  const navColors = navigationColors(theme);
  return {
    padding: theme.spacing(2),
    background: navColors.background,
    borderTopLeftRadius: theme.shape.borderRadius * 2,
    borderTopRightRadius: theme.shape.borderRadius * 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative'
  };
});

// Área de mensajes con scroll manual (quitamos scrollBehavior: 'smooth')
const MessageArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  flexGrow: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  '&::-webkit-scrollbar': {
    width: 6,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: alpha(theme.palette.mode === 'light' ? '#000' : '#FFF', 0.4),
    borderRadius: 3,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  }
}));

// Componente Message con radio adaptativo según el largo del contenido
const Message = styled(Box)(({ theme, variant, content }) => {
  const navColors = navigationColors(theme);

  const getMessageRadius = (messageContent) => {
    const contentLength = messageContent?.length || 0;
    if (contentLength < 20) {
      return '9999px';
    } else if (contentLength < 50) {
      return '24px';
    } else {
      return '16px';
    }
  };

  const baseStyles = {
    maxWidth: '80%',
    padding: theme.spacing(1.5),
    borderRadius: getMessageRadius(content),
    color: navColors.textDefault,
    textTransform: 'none'
  };

  if (variant === 'user') {
    return {
      ...baseStyles,
      alignSelf: 'flex-end',
      background: navColors.cardBackground,
    };
  }

  return {
    ...baseStyles,
    alignSelf: 'flex-start',
    background: 'transparent',
    color: theme.palette.mode === 'light' ? '#000000' : '#FFFFFF',
  };
});

// Contenedor de prompts para mobile
const PromptContainerMobile = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  marginBottom: theme.spacing(1),
  overflowX: 'auto',
  whiteSpace: 'nowrap',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  gap: theme.spacing(2),
  '&::-webkit-scrollbar': {
    display: 'none'
  }
}));

// Contenedor de prompts para desktop
const PromptContainerDesktop = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap'
}));

// Tarjeta de prompt
const PromptCard = styled(Box)(({ theme }) => {
  const navColors = navigationColors(theme);
  return {
    background: navColors.cardBackground,
    color: navColors.textDefault,
    padding: theme.spacing(1),
    borderRadius: '9999px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0
  };
});

// Texto de footer
const FooterText = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(1),
  fontSize: '0.65rem',
  color: '#808080'
}));

// Estilos para el contenido Markdown, ajustando también las listas
const MarkdownContent = styled(Box)(({ theme }) => {
  const navColors = navigationColors(theme);
  return {
    fontSize: '0.875rem',
    lineHeight: 1.43,
    '& > *:first-of-type': {
      marginTop: 0
    },
    '& > *:last-child': {
      marginBottom: 0
    },
    '& p': {
      margin: '0 0 1em 0', // Añadido margen inferior para separar párrafos
      color: navColors.textDefault,
      fontSize: '0.875rem',
      lineHeight: 1.43,
      whiteSpace: 'pre-line' // Respeta saltos de línea en párrafos
    },
    '& ul, & ol': {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1.5), // Añadido espacio después de listas
      paddingLeft: theme.spacing(2),
      '& li': {
        fontSize: '0.875rem',
        marginBottom: '0.5em', // Aumentado espacio entre items de listas
        '&::marker': {
          fontSize: '0.875rem',
          color: navColors.textDefault
        }
      }
    },
    '& code': {
      backgroundColor: alpha(navColors.textDefault, 0.1),
      padding: '2px 4px',
      borderRadius: 4,
      fontSize: '0.875rem'
    },
    '& pre': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(navColors.textDefault, 0.15)
        : alpha(navColors.textDefault, 0.1),
      padding: theme.spacing(1),
      margin: theme.spacing(1, 0), // Añadido margen para código
      borderRadius: 4,
      overflow: 'auto',
      whiteSpace: 'pre-wrap', // Asegura que el texto de código respete saltos
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
        fontSize: '0.875rem',
        color: theme.palette.mode === 'dark' ? '#e6e6e6' : 'inherit',
        whiteSpace: 'pre-wrap'
      }
    },
    '& blockquote': {
      borderLeft: `4px solid ${alpha(navColors.textDefault, 0.2)}`,
      margin: theme.spacing(1, 0),
      padding: theme.spacing(0.5, 1), // Ajustado padding para citas
      fontSize: '0.875rem'
    },
    '& a': {
      color: navColors.accent,
      textDecoration: 'none',
      fontSize: '0.875rem',
      '&:hover': {
        textDecoration: 'underline'
      }
    },
    '& h1': {
      fontSize: '1.2rem',
      fontWeight: 600,
      margin: theme.spacing(1.5, 0, 1, 0) // Top, right, bottom, left
    },
    '& h2': {
      fontSize: '1.1rem',
      fontWeight: 600,
      margin: theme.spacing(1.5, 0, 1, 0)
    },
    '& h3': {
      fontSize: '1rem',
      fontWeight: 600,
      margin: theme.spacing(1.2, 0, 0.8, 0)
    },
    '& hr': {
      margin: theme.spacing(1.5, 0),
      border: 0,
      borderTop: `1px solid ${alpha(navColors.textDefault, 0.2)}`
    },
    '& br': {
      display: 'block', // Asegura que los saltos de línea se muestren
      content: '""',
      marginTop: '0.5em' // Añade algo de espacio para los saltos de línea
    },
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      margin: theme.spacing(1, 0)
    }
  };
});

// FUNCION: renderMessageContent actualizada para el asistente médico
const renderMessageContent = (message, navColors, assistantTextColor, isNewMessage) => {
  if (message.type === 'assistant') {
    // Aseguramos que los saltos de línea del markdown se procesen correctamente
    return (
      <MarkdownContent className="markdown _markdown_1frq2_10">
        <FormattedAnimatedMessage content={message.content} isPlaying={isNewMessage} />
      </MarkdownContent>
    );
  }

  // Para mensajes del usuario, implementamos un manejo básico de saltos de línea
  return (
    <Typography 
      variant="body2" 
      sx={{ 
        color: assistantTextColor, 
        textTransform: 'none',
        whiteSpace: 'pre-line' // Permite que los saltos de línea se muestren correctamente
      }}
    >
      {message.content}
    </Typography>
  );
};

// Componente principal AGTFloatingChat
const AGTFloatingChat = () => {
  const theme = useTheme();
  const navColors = navigationColors(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isExpanded, setIsExpanded] = useState(false); // Inicialmente colapsado
  const { t } = useTranslation(); // Hook de i18next para traducciones
  const [messages, setMessages] = useState([
    { 
      type: 'assistant', 
      content: t('greeting') // Mensaje de bienvenida traducido
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const messageAreaRef = useRef(null);
  const inputRef = useRef(null);
  const shouldFocusRef = useRef(false); // Ref para controlar cuando debemos hacer focus
  const { latestMessageId, updateLatestMessage } = useLatestMessage();
  const assistantTextColor = theme.palette.mode === 'light' ? '#000000' : '#FFFFFF';
  const arrowColor = inputValue.trim() ? 'white' : navColors.textSecondary;
  // Bandera para controlar si se debe hacer scroll al añadir un mensaje
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);

  // Función para hacer scroll hasta el final
  const scrollToBottom = useCallback(() => {
    if (messageAreaRef.current) {
      messageAreaRef.current.scrollTop = messageAreaRef.current.scrollHeight;
    }
  }, []);

  // Efecto para hacer autofocus en el input cuando se abre el chat
  useEffect(() => {
    if (isExpanded && inputRef.current && shouldFocusRef.current) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          inputRef.current.focus();
          shouldFocusRef.current = false;
        }, 50);
      });
    }
  }, [isExpanded]);

  // Efecto para hacer scroll al fondo cuando se abre el chat
  useEffect(() => {
    if (isExpanded && messageAreaRef.current) {
      scrollToBottom();
    }
  }, [isExpanded, scrollToBottom]);

  // Efecto para realizar scroll solo cuando es necesario
  useEffect(() => {
    if (shouldScrollToBottom) {
      scrollToBottom();
      setShouldScrollToBottom(false);
    }
  }, [shouldScrollToBottom, scrollToBottom]);

  // Efecto para actualizar el último mensaje
  useEffect(() => {
    if (messages.length > 0) {
      updateLatestMessage(messages);
    }
  }, [messages, updateLatestMessage]);

  // Opciones de prompts para el asistente médico (ahora con traducciones)
  const promptOptions = [
    { 
      label: t('nextAppointments'), 
      icon: <Calendar size={16} /> 
    },
    { 
      label: t('healthMetrics'), 
      icon: <Activity size={16} /> 
    },
    { 
      label: t('prescribedMeds'), 
      icon: <Pill size={16} /> 
    }
  ];

  const handleExpand = (e) => {
    e.stopPropagation();
    if (!isExpanded) {
      shouldFocusRef.current = true; // Activamos la bandera para hacer focus
      setIsExpanded(true);
    }
  };

  const handlePromptClick = async (promptText) => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      setMessages((prev) => [...prev, { type: 'user', content: promptText }]);
      // Indicar que se debe hacer scroll al fondo después de añadir el mensaje del usuario
      setShouldScrollToBottom(true);
      
      const response = await agthealthMedicalAssistantService.sendQuery(promptText);
      setMessages((prev) => [
        ...prev,
        { type: 'assistant', content: response.success ? response.message : t('errorProcessing') }
      ]);
      // NO hacemos scroll después de añadir la respuesta del asistente
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          type: 'assistant',
          content: t('errorOccurred')
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    try {
      setIsLoading(true);
      setMessages((prev) => [...prev, { type: 'user', content: inputValue }]);
      // Indicar que se debe hacer scroll al fondo después de añadir el mensaje del usuario
      setShouldScrollToBottom(true);
      setInputValue('');
      
      const response = await agthealthMedicalAssistantService.sendQuery(inputValue);
      setMessages((prev) => [
        ...prev,
        { type: 'assistant', content: response.success ? response.message : t('errorProcessing') }
      ]);
      // NO hacemos scroll después de añadir la respuesta del asistente
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          type: 'assistant',
          content: t('errorOccurred')
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      <GlobalStyles />
      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: theme.zIndex.drawer + 1
        }}
      >
        <ClickAwayListener onClickAway={() => setIsExpanded(false)}>
          <Box>
            {isExpanded && (
              <Box sx={{ marginBottom: 2 }}>
                <ChatContainer onMouseDown={(e) => e.stopPropagation()}>
                  <ChatHeader>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Stethoscope size={20} style={{ marginRight: 8, color: navColors.accent }} />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          textTransform: 'none',
                          color: navColors.textDefault,
                          fontWeight: 500
                        }}
                      >
                        AGT Health
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsExpanded(false);
                      }}
                      sx={{
                        color: navColors.textDefault,
                        position: 'absolute',
                        right: theme.spacing(2)
                      }}
                    >
                      <Minimize2 size={18} />
                    </IconButton>
                  </ChatHeader>

                  <MessageArea ref={messageAreaRef} onMouseDown={(e) => e.stopPropagation()}>
                    {messages.map((message, index) => (
                      <Message key={index} variant={message.type} content={message.content}>
                        {renderMessageContent(
                          message,
                          navColors,
                          assistantTextColor,
                          index === latestMessageId
                        )}
                      </Message>
                    ))}
                    {isLoading && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <Box sx={{ transform: 'scale(0.66)' }}>
                          <AppleSpinner />
                        </Box>
                      </Box>
                    )}
                    <div ref={messagesEndRef} />
                  </MessageArea>

                  {messages.length === 1 &&
                    (isMobile ? (
                      <PromptContainerMobile onMouseDown={(e) => e.stopPropagation()}>
                        {promptOptions.map((option, idx) => (
                          <PromptCard
                            key={idx}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              handlePromptClick(option.label);
                            }}
                          >
                            {option.icon}
                            <Typography variant="body2" sx={{ textTransform: 'none' }}>
                              {option.label}
                            </Typography>
                          </PromptCard>
                        ))}
                      </PromptContainerMobile>
                    ) : (
                      <PromptContainerDesktop onMouseDown={(e) => e.stopPropagation()}>
                        {promptOptions.map((option, idx) => (
                          <PromptCard
                            key={idx}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              handlePromptClick(option.label);
                            }}
                          >
                            {option.icon}
                            <Typography variant="body2" sx={{ textTransform: 'none' }}>
                              {option.label}
                            </Typography>
                          </PromptCard>
                        ))}
                      </PromptContainerDesktop>
                    ))}

                  <Box sx={{ p: 1 }}>
                  </Box>

                  <FooterText>
                    {t('disclaimerText')}
                  </FooterText>
                </ChatContainer>
              </Box>
            )}

            {/* Input flotante con manejo mejorado del click */}
            <UnifiedInputButton
              isexpanded={isExpanded.toString()}
              onMouseDown={handleExpand} // Usamos la nueva función de manejo específica
            >
              <StyledInputBase
                inputRef={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('askAGTHealth')}
                disabled={!isExpanded}
                multiline
                maxRows={4}
                isexpanded={isExpanded.toString()}
                sx={{ pointerEvents: isExpanded ? 'auto' : 'none' }}
              />
              {isExpanded ? (
                <CircularSendButton
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleSendMessage();
                  }}
                  disabled={!inputValue.trim() || isLoading}
                >
                  <ArrowUp size={20} color={arrowColor} />
                </CircularSendButton>
              ) : (
                <ArrowUp size={20} color={arrowColor} />
              )}
            </UnifiedInputButton>
          </Box>
        </ClickAwayListener>
      </Box>
    </>
  );
};

export default AGTFloatingChat;